import './modal.scss';

import {
  KeyboardEvent,
  MouseEvent,
  PropsWithChildren,
  useCallback,
  useEffect,
  useState,
} from 'react';
import ReactDOM from 'react-dom';

import { makeKeyDownHandler } from '../../data/utils/make-key-down-handler';
import { formatTestId } from '../../data/utils/string.utils';
import { CustomFormattedMessage } from '../intl/custom-formatted-message';
import { Spinner } from '../spinner/spinner';

export enum ModalWidth {
  SMALL = 500,
  LARGE = 720,
  EXTRALARGE = 965,
  FULL = '90vw',
}

export type ModalProps = PropsWithChildren<{
  testId?: string;
  loading?: boolean;
  className?: JSX.Element | string;
  width?: ModalWidth | number;
  title?: JSX.Element | string;
  buttons?: JSX.Element;
  onClose?: () => void;
}>;

export function Modal({
  testId,
  loading = false,
  className,
  width,
  title,
  buttons,
  onClose,
  ...props
}: ModalProps) {
  const container = document.getElementById('portal-container');

  const getTestId = useCallback(
    (testIdType: string | string[] | null = null) =>
      formatTestId('modal', testId, testIdType),
    [testId]
  );

  const [active, setActive] = useState<boolean | null>(null);

  useEffect(() => {
    if (active === null) {
      setTimeout(() => {
        setActive(true);
      }, 100);
    }
  });

  const onHandleClick = useCallback(
    (e: MouseEvent<HTMLElement> | KeyboardEvent<HTMLElement>) => {
      const target = e.target as HTMLElement;

      if (
        target.tagName !== 'INPUT' ||
        (target.tagName === 'INPUT' && target.getAttribute('type') === 'text')
      ) {
        e.preventDefault();
        e.stopPropagation();
      }
    },
    []
  );

  const onHandleClose = useCallback(
    (e: MouseEvent<HTMLElement> | KeyboardEvent<HTMLElement>) => {
      e.preventDefault();
      e.stopPropagation();

      onClose?.();
    },
    [onClose]
  );

  if (!container) return null;

  return ReactDOM.createPortal(
    <div className="modal-container">
      <div
        data-test-id={getTestId()}
        className={`modal${width === ModalWidth.FULL ? ' modal--full' : ''}${
          className ? ` modal--${className}` : ''
        }${active ? ' active' : ''}`}
        style={{
          width: width ?? undefined,
          height: width === ModalWidth.FULL ? '90vh' : 'auto',
        }}
        role="button"
        tabIndex={0}
        onClick={onHandleClick}
        onKeyDown={makeKeyDownHandler(onHandleClick)}
      >
        {loading ? (
          <div className="modal__loading">
            <Spinner />
          </div>
        ) : (
          <>
            <button
              data-test-id={getTestId('close')}
              type="button"
              className="close"
              onClick={onHandleClose}
            >
              <CustomFormattedMessage id="common.close" />
            </button>

            {title && (
              <div data-test-id={getTestId('header')} className="modal__header">
                <h2 data-test-id={getTestId(['header', 'title'])}>{title}</h2>
              </div>
            )}

            {props.children && (
              <div
                data-test-id={getTestId('content')}
                className="modal__content"
              >
                {props.children}
              </div>
            )}

            {buttons && (
              <div data-test-id={getTestId('footer')} className="modal__footer">
                {buttons}
              </div>
            )}
          </>
        )}
      </div>
      <div
        data-test-id={getTestId('backdrop')}
        className="modal-backdrop"
        role="button"
        tabIndex={0}
        onClick={onHandleClose}
        onKeyDown={makeKeyDownHandler(onHandleClose)}
      >
        <CustomFormattedMessage id="common.close" />
      </div>
    </div>,
    container
  );
}
